import { Controller } from "@hotwired/stimulus"

// Drives the widget builder form (organizations/dashboard_widgets/_form.html.erb): keeps the
// group_by options, the aggregation row, and which filter inputs are visible all in sync with
// whichever dataset/display type is currently picked, and live-updates the preview panel as the
// form changes, by asking the server to re-run DashboardWidgetQuery against the form's current
// (unsaved) values and swapping the rendered result in, the same "server computes, JS only swaps"
// idiom live_chart_controller.js already uses for its own live chart, not a client-side
// reimplementation of the query engine's own logic.
//
// datasetsValue/labelsValue are the server's own DashboardWidgetQuery.client_config/FIELD_LABELS,
// embedded as JSON on the form: this controller never hardcodes a dataset's own dimensions or
// filters itself, so the two can never quietly drift apart.
export default class extends Controller {
  static values = { datasets: Object, labels: Object, previewUrl: String }
  static targets = ["dataset", "displayType", "groupByRow", "groupBy", "aggregationRow", "aggregation", "filterRow", "preview"]

  connect() {
    this.syncFieldVisibility()
  }

  // The one handler for the whole form (wired via a single form-level data-action covering every
  // input/change, see the form partial): re-syncs which fields are shown/populated for whatever
  // is currently picked, then debounces asking the server for a fresh preview. Re-syncing on every
  // change rather than only from the dataset/display-type selects' own listeners keeps this to one
  // handler instead of juggling several differently-scoped ones.
  fieldsChanged() {
    this.syncFieldVisibility()
    this.refreshPreview()
  }

  // Hiding a row is not enough on its own: a <select>/<input> still submits whatever value it
  // last held even while its wrapping row sits `hidden`, so every field this hides also gets its
  // own value cleared right here, not just visually tucked away. Skipping that step is exactly
  // what silently broke the live preview the first time this was written: switching to a
  // big_number display still left group_by carrying a leftover dimension from a moment earlier,
  // which DashboardWidget's own group_by_matches_display_type_and_dataset validation correctly
  // rejects: server-side safety doing its job, just surfacing as a confusing stuck preview
  // instead of the actual leftover-field bug it was.
  syncFieldVisibility() {
    const definition = this.datasetsValue[this.datasetTarget.value]
    const displayType = this.displayTypeTarget.value
    const isTimeSeries = displayType === "time_series"

    if (definition) {
      const previousGroupBy = this.groupByTarget.value
      this.groupByTarget.innerHTML = ""
      // A real blank option, not just an absent one: .value = "" below (used to clear this
      // select when the display type isn't table) only reliably lands on a blank state if one
      // genuinely exists to select; without it, clearing an already-populated <select> is
      // browser-dependent instead of guaranteed.
      this.groupByTarget.appendChild(document.createElement("option"))
      definition.dimensions.forEach((dimension) => {
        const option = document.createElement("option")
        option.value = dimension
        option.textContent = this.labelsValue[dimension] || dimension
        if (dimension === previousGroupBy) option.selected = true
        this.groupByTarget.appendChild(option)
      })

      // avg_duration is never valid for time_series regardless of dataset (unchanged); sum/avg
      // need the dataset's own value_column, time_series or not -- that's the whole point of
      // allowing them there at all (a revenue- or signups-by-day trend chart), unlike
      // avg_duration, which has no meaningful by-day version.
      const avgDurationOption = this.aggregationTarget.querySelector('option[value="avg_duration"]')
      const avgAcknowledgeOption = this.aggregationTarget.querySelector('option[value="avg_acknowledge"]')
      const sumOption = this.aggregationTarget.querySelector('option[value="sum"]')
      const avgOption = this.aggregationTarget.querySelector('option[value="avg"]')
      avgDurationOption.hidden = !definition.supports_duration || isTimeSeries
      // Time to acknowledge only exists for detected incidents, and like a duration has no by-day chart.
      avgAcknowledgeOption.hidden = !definition.supports_acknowledge || isTimeSeries
      sumOption.hidden = !definition.value_column
      avgOption.hidden = !definition.value_column
      ;[ avgDurationOption, avgAcknowledgeOption, sumOption, avgOption ].forEach((option) => {
        if (option.hidden && this.aggregationTarget.value === option.value) this.aggregationTarget.value = "count"
      })

      this.filterRowTargets.forEach((row) => {
        const supported = definition.filters.includes(row.dataset.filterKey)
        row.hidden = !supported
        if (!supported) row.querySelector("input").value = ""
      })
    }

    const isTable = displayType === "table"
    this.groupByRowTarget.hidden = !isTable
    if (!isTable) this.groupByTarget.value = ""

    // Previously always hidden and forced to count for time_series, back when count was the
    // only aggregation a chart could ever use; shown now whenever the current dataset has a
    // value_column, so sum/count/average are all real choices there too.
    const hasValueAggregationChoice = !!(definition && definition.value_column)
    this.aggregationRowTarget.hidden = isTimeSeries && !hasValueAggregationChoice
    if (isTimeSeries && !hasValueAggregationChoice) this.aggregationTarget.value = "count"
  }

  refreshPreview() {
    clearTimeout(this.previewTimer)
    this.previewTimer = setTimeout(() => this.fetchPreview(), 300)
  }

  // A dropped/failed preview request just leaves the last-known preview showing on screen; not
  // worth interrupting anyone's typing over what's still just a live preview, not a save; the
  // real validation happens for real on submit regardless of whether any given preview request
  // landed. Still logged, though (unlike leaving this a bare empty catch): a silent failure here
  // once already hid a real bug: this.element is the controller's own row, not the <form>
  // (see the form partial's own comment on why data-controller lives up there instead), so
  // FormData needs the actual <form> queried out of it, not passed this.element directly.
  async fetchPreview() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const form = this.element.querySelector("form")

    // The edit form carries Rails' hidden _method=patch field, which would turn this preview POST
    // into a PATCH to the widget's own update route; the preview is always a plain POST.
    const body = new FormData(form)
    body.delete("_method")

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { Accept: "text/html", "X-CSRF-Token": token },
        body
      })
      // A redirect means the request was turned away (signed out, say) and followed to a whole
      // page; only a real preview fragment belongs in the card.
      if (response.ok && !response.redirected) this.previewTarget.innerHTML = await response.text()
    } catch (error) {
      console.error("Dashboard widget preview request failed:", error)
    }
  }
}
